import React, { useState } from "react";
import {
  Paper,
  Grid,
  IconButton,
  Button,
  Typography,
  Box,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { uploadImageAndGetUrl } from "src/utils/uploadImage";

export default function InAppLinkForm({
  component,
  onUpdate,
  onDelete,
  onUpdateHandler,
  disabled = false,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: component.homeComponentItemId,
    });

  const [uploading, setUploading] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleVisibleChange = (e) => {
    onUpdate(component.homeComponentItemId, {
      ...component,
      visible: e.target.value === "true",
    });
  }

  const handleInAppPageChange = (e) => {
    onUpdate(component.homeComponentItemId, {
      ...component,
      inAppPage: e.target.value,
    });
  }

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!validTypes.includes(file.type)) {
      alert("Only JPG, JPEG, PNG, and WEBP formats are allowed.");
      event.target.value = null;
      return;
    }

    if (file.size > maxSize) {
      alert("File size should not exceed 2MB.");
      event.target.value = null;
      return;
    }
    setUploading(true);
    try {
      const imageUrl = await uploadImageAndGetUrl(file);
      onUpdate(component.homeComponentItemId, {
        ...component,
        imageUrl: imageUrl,
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Error uploading image. Please try again.");
    } finally {
      setUploading(false);
      event.target.value = null;
    }
  };

  const isIdString = typeof component.homeComponentItemId === "string";
  const isDisabled = disabled || isIdString;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0.5px 3px 10px 0px rgba(119,119,119,0.1)",
        mb: 2,
        p: 2,
      }}
    >
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{ minHeight: "80px" }}
      >
        {/* Drag Handle */}
        <Grid item xs={1}>
          <IconButton
            {...attributes}
            {...listeners}
            disabled={disabled}
            sx={{
              cursor: disabled ? "not-allowed" : "grab",
              color: disabled ? "#ccc" : "#7F63F4",
              "&:active": {
                cursor: disabled ? "not-allowed" : "grabbing",
              },
            }}
          >
            <DragIndicatorIcon />
          </IconButton>
        </Grid>
        {/* Image Column */}
        <Grid item xs={2} sx={{ paddingLeft: "0px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: "130px",
                height: "30px",
                border: "1px solid #E0E0E0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#F5F6F8",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {component.imageUrl ? (
                <img
                  src={component.imageUrl}
                  alt="Banner Ad"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "#9E9E9E", fontSize: "12px" }}
                >
                  375x75
                </Typography>
              )}
            </Box>
            <IconButton
              component="label"
              disabled={disabled || uploading}
              sx={{
                color: "#3167ca",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "rgba(49, 103, 202, 0.08)",
                },
              }}
            >
              <CloudUploadIcon />
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </IconButton>
          </Box>
        </Grid>

        {/* Click Visible Column */}
        <Grid item xs={3} sx={{ marginLeft: "30px !important" }}>
          <FormControl fullWidth size="small" variant="standard">
            <Select
              value={component.visible ? "true" : "false"}
              onChange={handleVisibleChange}
              disabled={disabled}
              sx={{
                fontSize: "16px",
                fontWeight: 500,
                color: "#667380",
                "&:before": {
                  borderBottom: "1px solid #E0E0E0",
                },
                "&:hover:before": {
                  borderBottom: "1px solid #667380",
                },
                "&:after": {
                  borderBottom: "2px solid #667380",
                },
                "& .MuiSelect-select": {
                  paddingBottom: "8px",
                },
              }}
            >
              <MenuItem value="true">True</MenuItem>
              <MenuItem value="false">False</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={3} sx={{ marginLeft: "30px !important" }}>
          <FormControl fullWidth size="small" variant="standard">
            <Select
              value={component.inAppPage ?? ""}
              onChange={handleInAppPageChange}
              disabled={disabled}
              sx={{
                fontSize: "16px",
                fontWeight: 500,
                color: "#667380",
                "&:before": {
                  borderBottom: "1px solid #E0E0E0",
                },
                "&:hover:before": {
                  borderBottom: "1px solid #667380",
                },
                "&:after": {
                  borderBottom: "2px solid #667380",
                },
                "& .MuiSelect-select": {
                  paddingBottom: "8px",
                },
              }}
            >
              <MenuItem value="account">Account </MenuItem>
              <MenuItem value="challenges">Challenges</MenuItem>
              <MenuItem value="coins">Coins</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Action Buttons */}
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            ml: "auto",
          }}
        >
          <Button
            variant="outlined"
            onClick={() =>
              onUpdateHandler(component.homeComponentItemId, component)
            }
            disabled={isDisabled}
            sx={{
              borderColor: "#3167ca",
              color: "#3167ca",
              fontWeight: 700,
              fontSize: "17.143px",
              borderRadius: "8px",
              boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
              minWidth: "107px",
              height: "47px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#3167ca",
                backgroundColor: "rgba(49, 103, 202, 0.04)",
              },
            }}
          >
            Update
          </Button>
          <IconButton
            onClick={() => onDelete(component.homeComponentItemId)}
            disabled={isDisabled}
            sx={{
              color: isDisabled ? "#ccc" : "#FF5274",
              "&:hover": {
                backgroundColor: isDisabled
                  ? "transparent"
                  : "rgba(255, 82, 116, 0.08)",
              },
            }}
          >
            <RemoveCircleIcon sx={{ fontSize: "30px" }} />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
}
